/**
 * ClientPortalLeaseSecurityChart Hook
 *
 * Renders a single segmented horizontal bar showing lease value distribution
 * by remaining term, with a legend below (colored dot, bucket label, percentage).
 *
 * Color scheme:
 * - Dark Red (#991B1B): Expired (conditionally shown as a leading segment)
 * - Red (#E11D48):      0-18 months
 * - Orange (#F97316):  19-36 months
 * - Blue (#0086E6):    37-54 months
 * - Green (#10B981):    55+ months
 */

import tippy from "tippy.js"

const SEGMENT_COLORS = [
  "#E11D48", // 0-18 months
  "#F97316", // 19-36 months
  "#0086E6", // 37-54 months
  "#10B981", // 55+ months
]

const EXPIRED_COLOR = "#991B1B"

export const ClientPortalLeaseSecurityChart = {
  mounted() {
    const chartData = JSON.parse(this.el.dataset.chartData || "[]")
    const i18n = JSON.parse(this.el.dataset.i18n || "null") || window.I18n || {}
    this.tippyInstances = []
    this.renderChart(chartData, i18n)

    this.handleEvent("update-lease-security-chart", ({ data }) => {
      this.renderChart(data, i18n)
    })
  },

  destroyed() {
    this.cleanupTooltips()
  },

  cleanupTooltips() {
    if (this.tippyInstances) {
      this.tippyInstances.forEach((instance) => instance.destroy())
      this.tippyInstances = []
    }
  },

  renderChart(chartData, i18n) {
    this.cleanupTooltips()
    const t = (key) => i18n[key] || key
    const expiredLabel = i18n["Expired"] || "Expired"

    const total = chartData.reduce((sum, d) => sum + (d.total_value || 0), 0)
    const hasExpired =
      chartData.length > 0 && chartData[0].bucket === expiredLabel

    const segments = chartData.map((d, i) => {
      const isExpired = d.bucket === expiredLabel
      const color = isExpired
        ? EXPIRED_COLOR
        : SEGMENT_COLORS[(hasExpired ? i - 1 : i) % SEGMENT_COLORS.length]
      const pct = total > 0 ? (d.total_value / total) * 100 : 0
      return { ...d, color, pct, isExpired }
    })

    if (total === 0) {
      this.el.innerHTML = `
        <div class="flex flex-col gap-[16px]">
          <div class="flex w-full h-[16px] rounded-[99px] bg-gray-100"></div>
          <div class="flex flex-wrap gap-x-[24px] gap-y-[8px]">
            ${segments
              .map(
                (s) => `
              <div class="flex items-center gap-[6px] text-[length:0.8125rem] whitespace-nowrap">
                <span class="inline-block w-[8px] h-[8px] rounded-full shrink-0" style="background-color: ${s.color}"></span>
                <span class="text-gray-600">${this.escapeHtml(s.bucket)}</span>
                <span class="font-semibold text-black">0%</span>
              </div>`,
              )
              .join("")}
          </div>
        </div>`
      return
    }

    const visibleSegments = segments.filter((s) => s.pct > 0)

    const barHTML = visibleSegments
      .map(
        (s) => `
        <div
          class="lease-segment h-full"
          data-segment-bucket="${this.escapeHtml(s.bucket)}"
          style="width: ${s.pct}%; background-color: ${s.color};"
        ></div>`,
      )
      .join("")

    const legendHTML = segments
      .map(
        (s) => `
      <div class="flex items-center gap-[6px] text-[length:0.8125rem] whitespace-nowrap">
        <span class="inline-block w-[8px] h-[8px] rounded-full shrink-0" style="background-color: ${s.color}"></span>
        <span class="text-gray-600">${this.escapeHtml(s.bucket)}</span>
        <span class="font-semibold text-black">${this.formatPercent(s.pct)}</span>
      </div>`,
      )
      .join("")

    this.el.innerHTML = `
      <div class="flex flex-col gap-[16px]">
        <div class="flex w-full h-[16px] rounded-[99px] overflow-hidden gap-[2px]">
          ${barHTML}
        </div>
        <div class="flex flex-wrap gap-x-[24px] gap-y-[8px]">
          ${legendHTML}
        </div>
      </div>`

    const segmentEls = this.el.querySelectorAll(".lease-segment")
    segmentEls.forEach((el, idx) => {
      const segment = visibleSegments[idx]
      const content = this.buildTooltip(segment, t)
      this.tippyInstances.push(
        tippy(el, {
          content,
          allowHTML: true,
          placement: "top",
          arrow: true,
          theme: "light",
          delay: [200, 0],
          maxWidth: "none",
          interactive: true,
          appendTo: () => document.body,
        }),
      )
    })
  },

  formatPercent(pct) {
    if (pct > 0 && pct < 1) return "<1%"
    return `${Math.round(pct)}%`
  },

  escapeHtml(str) {
    if (str == null) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  },

  buildTooltip(segment, t) {
    const count = segment.location_count ?? segment.locations?.length ?? 0
    const isExpired = segment.isExpired
    const color = segment.color
    const locations = segment.locations || []

    if (count === 0) {
      const noLeasesText = isExpired
        ? t("No Leases Expired")
        : t("No Leases Expiring")
      return `<div style="padding: 12px 16px;">
        <div style="color: ${color}; font-size: 14px; font-weight: 500;">
          ${noLeasesText}
        </div>
      </div>`
    }

    const statusText = isExpired ? t("Expired") : t("Expiring")

    if (locations.length === 0) {
      return `<div style="padding: 12px 16px;">
        <div style="color: ${color}; font-size: 14px; font-weight: 500;">
          ${count.toLocaleString()} ${count === 1 ? t("Lease") : t("Leases")} ${statusText}
        </div>
      </div>`
    }

    const columnCount = count > 10 ? 3 : count > 5 ? 2 : 1

    let html = `<div style="padding: 12px 16px; max-width: ${columnCount > 1 ? columnCount * 280 + "px" : "auto"};">
      <div style="color: ${color}; font-size: 14px; font-weight: 500; margin-bottom: 12px;">
        ${count} ${count === 1 ? t("Lease") : t("Leases")} ${statusText}
      </div>
      <div style="columns: ${columnCount}; column-gap: 24px; max-height: 400px; overflow-y: auto;">`

    locations.forEach((loc, index) => {
      const name = this.escapeHtml(loc.name || t("Unknown Property"))
      const cityState = [loc.city, loc.state].filter(Boolean).join(", ")
      const dateLabel = isExpired ? t("Expired on") : t("Expires on")
      const expiresAt = loc.expires_at ? `${dateLabel} ${loc.expires_at}` : ""
      const size =
        loc.size && loc.area_unit_abbr
          ? loc.size.toLocaleString() + " " + loc.area_unit_abbr
          : ""
      const productType = loc.product_type || ""
      const sizeType = [size, productType].filter(Boolean).join(" | ")

      html += `<div style="break-inside: avoid; margin-bottom: ${index < locations.length - 1 ? "16px" : "0"};">
        <div style="font-size: 16px; font-weight: 600; color: #111827; margin-bottom: 2px;">
          ${name}
        </div>
        ${cityState ? `<div style="font-size: 13px; color: #9CA3AF; margin-bottom: 2px;">${this.escapeHtml(cityState)}</div>` : ""}
        ${expiresAt ? `<div style="font-size: 13px; color: #6B7280; margin-bottom: 2px;">${this.escapeHtml(expiresAt)}</div>` : ""}
        ${sizeType ? `<div style="font-size: 13px; color: #6B7280;">${this.escapeHtml(sizeType)}</div>` : ""}
      </div>`
    })

    html += "</div></div>"
    return html
  },
}
